import { useCallback, useEffect, useState } from 'react';
import { ListTodo, Plus, X, Trash2, Archive, RotateCcw, ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { PAPER } from './theme';

/**
 * DiaryTodos — shared per-project to-do list for the individual Site Diary
 * page (DayView). Sits where the weather card used to be.
 *
 * Lifecycle: open → ticked-done (stays visible, ticked) → archived (hidden).
 *   • The CARD lists every non-archived item with a checkbox. Ticking marks
 *     it done (it STAYS in the list, shown ticked); a ticked item gains an
 *     Archive button to hide it from the list.
 *   • Tapping the card opens the POPUP — the place to ADD (adding closes the
 *     popup), edit text, archive, delete, and restore archived items.
 *   • Admin + surveyor only (backend 403s everyone else).
 */
export const DiaryTodos = ({ projectId, projectRef }) => {
  const { api } = useAuth();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const fetchTodos = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await api.get(`/diary/todos?project_id=${projectId}`);
      setTodos(res.data || []);
    } catch (_e) { /* non-blocking */ }
    finally { setLoading(false); }
  }, [api, projectId]);

  useEffect(() => { fetchTodos(); }, [fetchTodos]);

  const toggleDone = async (todo) => {
    const next = !todo.done;
    setTodos(prev => prev.map(t => t.id === todo.id ? { ...t, done: next } : t));
    try {
      await api.patch(`/diary/todos/${todo.id}`, { done: next });
    } catch (_e) {
      toast.error('Failed to update to-do');
      fetchTodos();
    }
  };

  const archive = async (todo) => {
    setTodos(prev => prev.filter(t => t.id !== todo.id));
    try {
      await api.patch(`/diary/todos/${todo.id}`, { archived: true });
    } catch (_e) {
      toast.error('Failed to archive to-do');
      fetchTodos();
    }
  };

  const openCount = todos.filter(t => !t.done).length;

  return (
    <>
      <div
        className="mt-3 rounded-xl px-3.5 py-3 cursor-pointer transition-all hover:shadow-md"
        style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, boxShadow: PAPER.shadow }}
        onClick={() => setOpen(true)}
        data-testid="diary-todo-card"
      >
        <div className="flex items-center gap-2 mb-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: '#EDE9FE', color: '#6D28D9' }}
          >
            <ListTodo className="w-4 h-4" strokeWidth={2.25} />
          </div>
          <div className="text-[10px] uppercase tracking-wider font-semibold flex-1" style={{ color: PAPER.muted }}>
            To-dos {openCount > 0 && <span style={{ color: PAPER.hint }}>· {openCount}</span>}
          </div>
          <span className="text-[11px] font-medium flex items-center gap-1" style={{ color: PAPER.accent }}>
            <Plus className="w-3.5 h-3.5" /> Add / edit
          </span>
        </div>

        {loading ? (
          <div className="text-[12px] py-1.5" style={{ color: PAPER.muted }}>Loading…</div>
        ) : todos.length === 0 ? (
          <div className="text-[12px] py-1" style={{ color: PAPER.muted }}>No to-dos yet — tap to add one.</div>
        ) : (
          <div
            className="space-y-1.5 max-h-44 overflow-y-auto"
            data-testid="diary-todo-card-list"
          >
            {todos.map(t => (
              <div
                key={t.id}
                className="flex items-center gap-2"
                data-testid={`diary-todo-item-${t.id}`}
              >
                <input
                  type="checkbox"
                  checked={!!t.done}
                  className="w-4 h-4 flex-shrink-0 accent-[#6D28D9]"
                  onChange={() => toggleDone(t)}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`diary-todo-check-${t.id}`}
                />
                <span
                  className={`flex-1 text-[13px] leading-snug ${t.done ? 'line-through' : ''}`}
                  style={{ color: t.done ? PAPER.muted : PAPER.ink }}
                >
                  {t.text}
                </span>
                {t.done && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); archive(t); }}
                    className="text-[11px] font-medium flex items-center gap-1 px-2 py-1 rounded-md flex-shrink-0 transition-colors hover:bg-[#FAF7F2]"
                    style={{ color: PAPER.inkSoft, border: `1px solid ${PAPER.border}` }}
                    data-testid={`diary-todo-archive-${t.id}`}
                    title="Hide from the list"
                  >
                    <Archive className="w-3 h-3" /> Archive
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {open && (
        <TodoDialog
          title={projectRef ? `To-dos — ${projectRef}` : 'To-dos'}
          projectId={projectId}
          onClose={() => { setOpen(false); fetchTodos(); }}
        />
      )}
    </>
  );
};

/**
 * TodoDialog — the "input window" that expands from the card. Adding a to-do
 * closes the popup. Editing text, ticking, archiving, deleting and restoring
 * archived items happen here too (those keep the popup open).
 */
const TodoDialog = ({ title, projectId, onClose }) => {
  const { api } = useAuth();
  const [active, setActive] = useState([]);
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newText, setNewText] = useState('');
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        api.get(`/diary/todos?project_id=${projectId}`),
        api.get(`/diary/todos?project_id=${projectId}&archived_only=true`),
      ]);
      setActive(a.data || []);
      setArchived(b.data || []);
    } catch (_e) {
      toast.error('Failed to load to-dos');
    } finally { setLoading(false); }
  }, [api, projectId]);

  useEffect(() => { load(); }, [load]);

  const addTodo = async () => {
    const text = newText.trim();
    if (!text) return;
    setSaving(true);
    try {
      await api.post('/diary/todos', { project_id: projectId, text });
      setNewText('');
      onClose(); // per spec: adding a to-do closes the popup
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add to-do');
      setSaving(false);
    }
  };

  const setDoneState = async (t, doneVal) => {
    try { await api.patch(`/diary/todos/${t.id}`, { done: doneVal }); load(); }
    catch (_e) { toast.error('Failed to update to-do'); }
  };

  const setArchivedState = async (t, archivedVal) => {
    try { await api.patch(`/diary/todos/${t.id}`, { archived: archivedVal }); load(); }
    catch (_e) { toast.error('Failed to update to-do'); }
  };

  const saveText = async (t, text) => {
    const clean = (text || '').trim();
    if (!clean || clean === t.text) return;
    try { await api.patch(`/diary/todos/${t.id}`, { text: clean }); }
    catch (_e) { toast.error('Failed to save'); load(); }
  };

  const removeTodo = async (t) => {
    try { await api.delete(`/diary/todos/${t.id}`); load(); }
    catch (_e) { toast.error('Failed to delete to-do'); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(31,41,55,0.55)', backdropFilter: 'blur(4px)' }}
      data-testid="diary-todo-dialog"
      onClick={onClose}
    >
      <div
        className="max-w-md w-full rounded-2xl overflow-hidden flex flex-col"
        style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, boxShadow: '0 10px 30px rgba(0,0,0,0.3)', maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: `1px solid ${PAPER.border}` }}>
          <ListTodo className="w-4 h-4" style={{ color: '#6D28D9' }} />
          <div className="text-[15px] font-bold flex-1 truncate" style={{ color: PAPER.ink }}>{title}</div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-[#FAF7F2]"
            style={{ color: PAPER.muted }}
            data-testid="diary-todo-dialog-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Add input */}
        <div className="px-4 py-3 flex gap-2" style={{ background: PAPER.page, borderBottom: `1px solid ${PAPER.border}` }}>
          <input
            type="text"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addTodo(); }}
            placeholder="Add a to-do…"
            className="flex-1 text-[13px] rounded-lg px-3 py-2 focus:outline-none focus:border-[#6D28D9]"
            style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, color: PAPER.ink }}
            data-testid="diary-todo-new-input"
            autoFocus
          />
          <button
            type="button"
            onClick={addTodo}
            disabled={saving || !newText.trim()}
            className="text-[13px] font-semibold rounded-lg px-3.5 py-2 flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: PAPER.navy, color: PAPER.accent }}
            data-testid="diary-todo-add-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Add
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-3 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" style={{ color: PAPER.accent }} /></div>
          ) : (
            <>
              {active.length === 0 ? (
                <div className="text-[13px] text-center py-4" style={{ color: PAPER.muted }}>No to-dos on the list.</div>
              ) : (
                <div className="space-y-2" data-testid="diary-todo-dialog-active-list">
                  {active.map(t => (
                    <div key={t.id} className="flex items-center gap-2" data-testid={`diary-todo-dialog-item-${t.id}`}>
                      <input
                        type="checkbox"
                        checked={!!t.done}
                        className="w-4 h-4 flex-shrink-0 accent-[#6D28D9]"
                        onChange={() => setDoneState(t, !t.done)}
                        data-testid={`diary-todo-dialog-check-${t.id}`}
                      />
                      <input
                        type="text"
                        defaultValue={t.text}
                        onBlur={(e) => saveText(t, e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        className={`flex-1 text-[13px] rounded px-2 py-1.5 focus:outline-none focus:border-[#6D28D9] ${t.done ? 'line-through' : ''}`}
                        style={{ background: PAPER.page, border: `1px solid ${PAPER.border}`, color: t.done ? PAPER.muted : PAPER.ink }}
                        data-testid={`diary-todo-dialog-text-${t.id}`}
                      />
                      {t.done && (
                        <button
                          type="button"
                          onClick={() => setArchivedState(t, true)}
                          className="p-1.5 rounded hover:bg-[#FAF7F2] flex-shrink-0"
                          style={{ color: PAPER.inkSoft }}
                          title="Archive (hide from list)"
                          data-testid={`diary-todo-dialog-archive-${t.id}`}
                        >
                          <Archive className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeTodo(t)}
                        className="p-1.5 rounded hover:bg-red-50 flex-shrink-0"
                        style={{ color: PAPER.muted }}
                        title="Delete"
                        data-testid={`diary-todo-dialog-delete-${t.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Archived / restore */}
              {archived.length > 0 && (
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowArchived(s => !s)}
                    className="w-full flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold py-1"
                    style={{ color: PAPER.muted }}
                    data-testid="diary-todo-dialog-archived-toggle"
                  >
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showArchived ? 'rotate-180' : ''}`} />
                    Archived · {archived.length}
                  </button>
                  {showArchived && (
                    <div className="space-y-2 mt-1.5" data-testid="diary-todo-dialog-archived-list">
                      {archived.map(t => (
                        <div key={t.id} className="flex items-center gap-2">
                          <span className="flex-1 text-[13px] line-through" style={{ color: PAPER.muted }}>{t.text}</span>
                          <button
                            type="button"
                            onClick={() => setArchivedState(t, false)}
                            className="text-[11px] font-medium flex items-center gap-1 px-2 py-1 rounded-md"
                            style={{ background: PAPER.paperMuted, color: PAPER.inkSoft }}
                            data-testid={`diary-todo-dialog-restore-${t.id}`}
                          >
                            <RotateCcw className="w-3 h-3" /> Restore
                          </button>
                          <button
                            type="button"
                            onClick={() => removeTodo(t)}
                            className="p-1.5 rounded hover:bg-red-50 flex-shrink-0"
                            style={{ color: PAPER.muted }}
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiaryTodos;
